# OPCS-4 API

Look up OPCS-4 (Office of Population Censuses and Surveys Classification of Interventions and Procedures) codes used by NHS England.

## Overview

OPCS-4 is the standard procedure classification used by NHS England for hospital episode statistics, clinical coding, and reimbursement. FHIRfly provides access to OPCS-4.11, published under the Open Government Licence v3.0 (Crown copyright).

## Endpoints

| Method | Path | Description |
|--------|------|-------------|
| GET | `/v1/opcs4/:code` | Single OPCS-4 code lookup |
| POST | `/v1/opcs4/_batch` | Batch lookup (up to 100) |
| GET | `/v1/opcs4/shapes` | List available response shapes |

## Single Lookup

<ApiExample
  endpoint="/v1/opcs4/W37.1"
  method="GET"
  title="Look up primary hip replacement"
  response={{
    "data": {
      "code": "W37.1",
      "display": "Primary total prosthetic replacement of hip joint using cement",
      "chapter": "W",
      "chapter_title": "Other bones and joints of skeleton",
      "category": "W37",
      "category_title": "Total prosthetic replacement of hip joint using cement",
      "version": "4.11",
      "fhir_coding": {
        "code": "W37.1",
        "display": "Primary total prosthetic replacement of hip joint using cement",
        "system": "https://fhir.nhs.uk/Id/opcs-4"
      }
    },
    "meta": {
      "legal": {
        "attribution_required": true,
        "citation": "The OPCS Classification of Interventions and Procedures, Crown copyright. Open Government Licence v3.0.",
        "license": "OGL-UK-3.0",
        "source_name": "NHS England OPCS-4"
      }
    }
  }}
/>

## Common OPCS-4 Codes

| Code | Description |
|------|-------------|
| W37.1 | Primary total hip replacement using cement |
| W40.1 | Primary total knee replacement using cement |
| G45.1 | Fibreoptic endoscopic examination of upper GI tract |
| K63.1 | Percutaneous transluminal coronary angioplasty |
| Y53.1 | Approach to organ under ultrasonic guidance |

## Batch Lookup

Look up multiple OPCS-4 codes in a single request (up to 100):

<ApiExample
  endpoint="/v1/opcs4/_batch"
  method="POST"
  title="Look up multiple procedure codes"
  requestBody={{
    "codes": ["W37.1", "W40.1", "G45.1"]
  }}
  response={{
    "count": 3,
    "results": [
      {
        "input": "W37.1",
        "code": "W37.1",
        "status": "ok",
        "data": {
          "display": "Primary total prosthetic replacement of hip joint using cement",
          "chapter": "W"
        }
      },
      {
        "input": "W40.1",
        "code": "W40.1",
        "status": "ok",
        "data": {
          "display": "Primary total prosthetic replacement of knee joint using cement",
          "chapter": "W"
        }
      },
      {
        "input": "G45.1",
        "code": "G45.1",
        "status": "ok",
        "data": {
          "display": "Fibreoptic endoscopic examination of upper gastrointestinal tract",
          "chapter": "G"
        }
      }
    ],
    "meta": {
      "legal": {
        "license": "OGL-UK-3.0",
        "attribution_required": true,
        "source_name": "NHS England OPCS-4"
      }
    }
  }}
/>

### Batch with `shape=full`

When using `shape=full` on batch requests, the response includes `meta.source` with provenance information:

```
POST /v1/opcs4/_batch?shape=full
```

```json
{
  "count": 3,
  "results": [
    {
      "input": "W37.1",
      "code": "W37.1",
      "status": "ok",
      "data": {
        "display": "Primary total prosthetic replacement of hip joint using cement",
        "chapter": "W"
      }
    }
  ],
  "meta": {
    "source": {
      "name": "NHS England OPCS-4",
      "url": "https://isd.digital.nhs.uk/trud/",
      "version": "4.11",
      "fhirfly_updated_at": "2026-03-01T03:00:00Z"
    },
    "legal": {
      "license": "OGL-UK-3.0",
      "attribution_required": true
    }
  }
}
```

## Code Format

OPCS-4 codes follow the pattern: a letter followed by digits and a decimal point.

| Input | Valid | Description |
|-------|-------|-------------|
| `W37.1` | Yes | Hip replacement |
| `G45.1` | Yes | Upper GI endoscopy |
| `K63.1` | Yes | Coronary angioplasty |
| `12345` | No | Numeric-only format |

## Response Shapes

Control the level of detail in responses with the `shape` query parameter:

| Shape | Description |
|-------|-------------|
| `compact` | Code, display, chapter |
| `standard` | + category, version, fhir_coding |
| `full` | + ingest metadata, provenance |

```bash
# Minimal data
GET /v1/opcs4/W37.1?shape=compact

# Standard data (default)
GET /v1/opcs4/W37.1?shape=standard

# Full data with provenance
GET /v1/opcs4/W37.1?shape=full
```

See [Response Shapes](/docs/response-shapes) for field details.

## License Notice

OPCS-4 data is Crown copyright and provided under the Open Government Licence v3.0. The `meta.legal` field in every response provides the required attribution text.

## Required Scopes

- `opcs4.read` - Single and batch lookups

## See Also

- [OPCS-4 Search](/docs/search-apis/opcs4) — Search OPCS-4 procedure codes
- [ICD-10 API](/docs/value-apis/icd10) — ICD-10 diagnosis and procedure codes (U.S.)
- [Clinical Data APIs Overview](/docs/value-apis/overview) — Overview of all lookup APIs

<DocsNextStep />
